import { z } from 'zod'
import {
  normalizeEmail,
  normalizeGender,
  normalizeMatchmakingId,
  normalizePhone,
  normalizeText,
} from './normalize'

/**
 * Input schemas shared by the admin UI and the public form.
 *
 * These are a usability layer, not the security boundary: every rule here is
 * also enforced by a CHECK constraint, trigger, or server-side check in the
 * database, because a schema running in a browser can always be bypassed.
 */

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** Three digits, as printed on the nametag. Accepts "7" and stores "007". */
export const matchmakingIdSchema = z
  .string()
  .transform((value) => normalizeMatchmakingId(value))
  .refine((value): value is string => value !== null && /^[0-9]{3}$/.test(value), {
    message: 'Enter the 3-digit number from your nametag',
  })

export const emailSchema = z
  .string()
  .transform((value) => normalizeEmail(value))
  .refine((value): value is string => value !== null && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value), {
    message: 'Enter a valid email address',
  })

/**
 * Optional on the admin side: an imported attendee record may legitimately have
 * no phone, and an organiser should not be blocked from saving one that does not.
 */
export const optionalPhoneSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value) => normalizePhone(value))
  .refine((value) => value === null || /^\+?[0-9]{5,20}$/.test(value), {
    message: 'Enter a valid phone number',
  })

export const requiredNameSchema = z
  .string()
  .transform((value) => normalizeText(value))
  .refine((value): value is string => value !== null && value.length <= 100, {
    message: 'Required, and at most 100 characters',
  })

export const optionalNameSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value) => normalizeText(value))
  .refine((value) => value === null || value.length <= 100, {
    message: 'At most 100 characters',
  })

/**
 * Required on the public form.
 *
 * The entire point of the match email is handing someone a name and a number.
 * Eventbrite rarely returns `cell_phone`, so if the form does not insist, most
 * matches end up with an empty Contact column and the export cannot do its job.
 * Asked for once, at the only moment the person is definitely present.
 */
export const requiredPhoneSchema = z
  .string()
  .transform((value) => normalizePhone(value))
  .refine((value): value is string => value !== null && /^\+?[0-9]{5,20}$/.test(value), {
    message: 'Enter a phone number so we can pass it to your matches',
  })

/** The options offered on the public form, stored lowercased as free text. */
export const GENDER_CHOICES = ['woman', 'man', 'non-binary'] as const

export const optionalGenderSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value) => normalizeGender(value))
  .refine((value) => value === null || value.length <= 50, {
    message: 'At most 50 characters',
  })

/**
 * The optional note at the end of the public form.
 *
 * 1000 characters, matching the column. Long enough for a number they wrote
 * down and a sentence explaining it, short enough that nobody pastes an essay
 * into an organiser's evening.
 */
export const optionalCommentSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value) => normalizeText(value))
  .refine((value) => value === null || value.length <= 1000, {
    message: 'At most 1000 characters',
  })

/**
 * Shared event passcode. Minimum 6 characters, matching set_form_passcode().
 * It is read aloud or printed on a card at the event, so it is a low-entropy
 * secret by design — the brute-force protection is the rate limiter, not length.
 */
export const passcodeSchema = z
  .string()
  .min(6, 'Passcode must be at least 6 characters')
  .max(128, 'Passcode must be at most 128 characters')

// ---------------------------------------------------------------------------
// Admin: events, attendees, form config
// ---------------------------------------------------------------------------

export const eventInputSchema = z
  .object({
    name: requiredNameSchema.refine((value) => value.length <= 200, {
      message: 'At most 200 characters',
    }),
    description: z.string().max(4000).optional().nullable(),
    displayOrgName: optionalNameSchema,
    logoUrl: z.url().startsWith('https://', 'Logo URL must use https').optional().nullable(),
    venue: z.string().max(200).optional().nullable(),
    startsAt: z.iso.datetime({ offset: true }).optional().nullable(),
    endsAt: z.iso.datetime({ offset: true }).optional().nullable(),
    timezone: z.string().min(1).max(64).default('UTC'),
  })
  .refine(
    (value) =>
      !value.startsAt || !value.endsAt || new Date(value.endsAt) >= new Date(value.startsAt),
    { message: 'End time must be after the start time', path: ['endsAt'] }
  )

export type EventInput = z.infer<typeof eventInputSchema>

export const attendeeInputSchema = z.object({
  firstName: requiredNameSchema,
  lastName: optionalNameSchema,
  email: z
    .string()
    .optional()
    .nullable()
    .transform((value) => normalizeEmail(value))
    .refine((value) => value === null || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value), {
      message: 'Enter a valid email address',
    }),
  phone: optionalPhoneSchema,
  gender: optionalGenderSchema,
  matchmakingId: matchmakingIdSchema.optional(),
  isVisible: z.boolean().default(true),
})

export type AttendeeInput = z.infer<typeof attendeeInputSchema>

export const formConfigSchema = z.object({
  /** "X" — the cap on how many people a submitter may choose. */
  maxSelections: z
    .number()
    .int('Must be a whole number')
    .min(1, 'Must allow at least one selection')
    .max(25, 'Cannot exceed 25 selections'),
  title: z.string().max(200).optional().nullable(),
  introText: z.string().max(4000).optional().nullable(),
  closingMessage: z.string().max(4000).optional().nullable(),
  allowResubmission: z.boolean().default(true),
})

export type FormConfig = z.infer<typeof formConfigSchema>

// ---------------------------------------------------------------------------
// Public form
// ---------------------------------------------------------------------------

export const formSessionRequestSchema = z.object({
  slug: z.string().min(8).max(64),
  passcode: z.string().min(1).max(128),
})

/**
 * The public submission. `maxSelections` is per-event, so this is a factory
 * rather than a constant. The same limit is re-checked server-side in
 * public_submit_form() — the client cannot raise its own ceiling.
 */
export function createSubmissionSchema(maxSelections: number) {
  return z.object({
    firstName: requiredNameSchema,
    lastName: optionalNameSchema,
    email: emailSchema,
    phone: requiredPhoneSchema,
    // Optional by design: an unanswerable required question is a poor way to
    // end someone's evening, and the reports group unanswered as 'unspecified'.
    gender: optionalGenderSchema,
    // The pick list only offers people the organiser imported, so this is where
    // a number that is not on it, or a question, has to go.
    comment: optionalCommentSchema,
    matchmakingId: matchmakingIdSchema,
    selections: z
      .array(z.uuid('Invalid selection'))
      .max(maxSelections, `Choose at most ${String(maxSelections)} people`)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'The same person was selected twice',
      })
      .default([]),
  })
}

export type SubmissionInput = z.infer<ReturnType<typeof createSubmissionSchema>>

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const credentialsSchema = z.object({
  email: emailSchema,
  // Long minimum because these accounts hold every attendee's contact details.
  password: z.string().min(12, 'Use at least 12 characters').max(128),
})

export type Credentials = z.infer<typeof credentialsSchema>
